import React from "react";
import { InitialDescriptionPhase } from "../InitialDescriptionPhase";
import { ObjectivesPhase } from "../ObjectivesPhase";
import { TechnicalSolutionPhase } from "../TechnicalSolutionPhase";
import { MilestonesPhase } from "../MilestonesPhase";
import { StoriesPhase } from "../StoriesPhase";
import { SchemaPhase } from "../SchemaPhase";
import { ResourcesPhase } from "../ResourcesPhase";
import { ProposalPhase } from "../ProposalPhase";
import type { ProjectPhaseWizardProps } from "../../projectDetails.types";
import { PROJECT_PHASES, PHASE_CONFIG } from "../../projectDetails.definitions";
import { useProjectMutations } from "../../../../hooks";
import type { TechnicalSolution, ProjectTimeline, ProjectStories, ProjectSchema, ProjectResource } from "@/features/Projects";

export default function ProjectPhaseWizard({ project, onPhaseChange }: ProjectPhaseWizardProps) {
  const { update } = useProjectMutations();

  const handleSaveDescription = async (description: string) => {
    await update({
      id: project.id,
      data: { description }
    });
  };

  const handleSaveObjectives = async (objectives: string[]) => {
    await update({
      id: project.id,
      data: { objectives }
    });
  };

  const handleSaveTechnicalSolution = async (technicalSolution: TechnicalSolution) => {
    try {
      await update({
        id: project.id,
        data: { technicalSolution }
      });
    } catch (error) {
      console.error("❌ Erro ao salvar no Firebase:", error);
      throw error;
    }
  };

  const handleSaveTimeline = async (timeline: ProjectTimeline) => {
    try {
      await update({
        id: project.id,
        data: { timeline }
      });
    } catch (error) {
      console.error("❌ Erro ao salvar timeline no Firebase:", error);
      throw error;
    }
  };

  const handleSaveStories = async (stories: ProjectStories) => {
    try {
      await update({
        id: project.id,
        data: { stories }
      });
    } catch (error) {
      console.error("❌ Erro ao salvar user stories no Firebase:", error);
      throw error;
    }
  };

  const handleSaveSchema = async (schema: ProjectSchema) => {
    try {
      await update({
        id: project.id,
        data: { schema }
      });
    } catch (error) {
      console.error("❌ Erro ao salvar schema no Firebase:", error);
      throw error;
    }
  };

  const handleSaveResources = async (resources: ProjectResource[], estimatedPrice: number) => {
    try {
      await update({
        id: project.id,
        data: { resources, estimatedPrice }
      });
    } catch (error) {
      console.error("❌ Erro ao salvar recursos no Firebase:", error);
      throw error;
    }
  };

  const handleNextPhase = () => {
    const currentOrder = PHASE_CONFIG[project.currentPhase].order;
    const nextPhase = PROJECT_PHASES.find(phase => 
      PHASE_CONFIG[phase].order === currentOrder + 1
    );
    if (nextPhase) {
      onPhaseChange(nextPhase);
    }
  };

  return (
    <div className="w-full max-w-7xl mx-auto space-y-6">
      {/* Current Phase Content */}
      <div className="bg-white rounded-xl shadow-sm border border-raro-gray-200 p-6">
        {/* Phase-specific content area */}
        {project.currentPhase === "initial_description" ? (
          <InitialDescriptionPhase
            project={project}
            onSave={handleSaveDescription}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "objectives" ? (
          <ObjectivesPhase
            project={project}
            onSave={handleSaveObjectives}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "technical_solution" ? (
          <TechnicalSolutionPhase
            project={project}
            onSave={handleSaveTechnicalSolution}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "milestones" ? (
          <MilestonesPhase
            project={project}
            onSave={handleSaveTimeline}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "stories" ? (
          <StoriesPhase
            project={project}
            onSave={handleSaveStories}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "schema" ? (
          <SchemaPhase
            project={project}
            onSave={handleSaveSchema}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "resources" ? (
          <ResourcesPhase
            project={project}
            onSave={handleSaveResources}
            onNext={handleNextPhase}
          />
        ) : project.currentPhase === "proposal" ? (
          <ProposalPhase
            project={project}
            onNext={handleNextPhase}
          />
        ) : (
          <div className="bg-raro-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-raro-gray-900 mb-2">
              Conteúdo da Fase
            </h3>
            <div className="text-sm text-raro-gray-600">
              {project.currentPhase === "description" && project.description ? (
                <div>
                  <p className="font-medium mb-2">Descrição do Projeto:</p>
                  <p className="bg-white p-3 rounded border">{project.description}</p>
                </div>
              ) : (
                <p className="italic">
                  O conteúdo desta fase será gerado quando ela for ativada.
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}